import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./useAuth";
import { useGeolocationPermission } from "./useGeolocationPermission";
import { useGhostModeAware } from "./useGhostModeAware";
import { useLocationTracker } from "./useLocationTracker";
import { useActiveCheckInState } from "./useActiveCheckInState";
import { logLocationTransition } from "@/lib/locationDebugLogger";

/**
 * Fase 4 — a localização só é coletada enquanto existe CHECK-IN ATIVO em um
 * estabelecimento, e apenas com o app em primeiro plano. Sem presença
 * confirmada no local, nada é lido nem persistido (nenhum prompt de permissão). Composto por:
 *  - useGeolocationPermission: estado da Permissions API
 *  - useGhostModeAware: lê e mantém o ghost_mode em referência
 *  - useLocationTracker: getCurrentPosition + watchPosition + interval
 */
const PROMPT_DISMISSED_KEY = "zappy.bg-location.prompt-dismissed";
const FALLBACK_DISMISSED_KEY = "zappy.bg-location.fallback-dismissed";

export type BackgroundLocationStatus =
  | "disabled"
  | "pending"
  | "denied"
  | "active"
  | "stale";

export interface UseBackgroundLocationResult {
  shouldPrompt: boolean;
  shouldShowFallback: boolean;
  requestPermission: () => Promise<PermissionState | "unknown">;
  dismissPrompt: () => void;
  dismissFallback: () => void;
  status: BackgroundLocationStatus;
  lastUpdateAt: number | null;
  permissionState: PermissionState | "unknown";
}

const defaultValue: UseBackgroundLocationResult = {
  shouldPrompt: false,
  shouldShowFallback: false,
  requestPermission: async () => "unknown",
  dismissPrompt: () => {},
  dismissFallback: () => {},
  status: "disabled",
  lastUpdateAt: null,
  permissionState: "unknown",
};

const BackgroundLocationContext =
  createContext<UseBackgroundLocationResult>(defaultValue);

const POSITION_MAX_AGE_MS = 5 * 60_000;
const FOREGROUND_UPDATE_INTERVAL_MS = 2 * 60_000;

const useBackgroundLocationInternal = (): UseBackgroundLocationResult => {
  const { user } = useAuth();
  const userId = user?.id;
  const ghostModeRef = useGhostModeAware(userId);
  const [permissionState, setPermissionState] = useGeolocationPermission(Boolean(userId));

  const { isCheckedIn, venueId, refresh } = useActiveCheckInState(userId);

  const isPaused = useCallback(
    () => ghostModeRef.current || !isCheckedIn,
    [ghostModeRef, isCheckedIn]
  );
  const trackingEnabled =
    Boolean(userId) && isCheckedIn && permissionState === "granted";
  const { lastUpdateAt, persistPosition } = useLocationTracker({
    userId,
    enabled: trackingEnabled,
    isPaused,
    venueId,
    onCheckedOut: refresh,
  });

  // Resolve estados ambíguos ("unknown"/"prompt") sondando a posição uma única vez.
  // Sem isso o badge "Aguardando localização" pode ficar girando para sempre quando
  // a Permissions API não está disponível ou o navegador retorna "prompt" mesmo
  // após o usuário ter negado a permissão em outro fluxo.
  const [probed, setProbed] = useState(false);
  useEffect(() => {
    if (!userId || probed) return;
    if (!isCheckedIn) return;
    if (permissionState === "granted" || permissionState === "denied") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    if (ghostModeRef.current) return;
    setProbed(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void persistPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        setPermissionState("granted");
      },
      (err) => {
        setPermissionState(err.code === err.PERMISSION_DENIED ? "denied" : "prompt");
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: POSITION_MAX_AGE_MS }
    );
  }, [userId, permissionState, probed, persistPosition, setPermissionState, ghostModeRef, isCheckedIn]);

  // ---- Retry com backoff exponencial enquanto o status estiver "pending" ----
  // Tenta resolver automaticamente: 2s → 4s → 8s → 16s → 32s (5 tentativas).
  // Reseta o contador quando sai de "pending".
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const probePositionForRetry = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    if (ghostModeRef.current || !isCheckedIn) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void persistPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        setPermissionState("granted");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setPermissionState("denied");
        }
        // timeout / position unavailable: deixa o próximo retry tentar
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: POSITION_MAX_AGE_MS }
    );
  }, [persistPosition, setPermissionState, ghostModeRef, isCheckedIn]);

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(PROMPT_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [fallbackDismissed, setFallbackDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(FALLBACK_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const requestPermission = useCallback<UseBackgroundLocationResult["requestPermission"]>(
    () =>
      new Promise((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          resolve("unknown");
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            void persistPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
            setPermissionState("granted");
            resolve("granted");
          },
          (err) => {
            const state: PermissionState =
              err.code === err.PERMISSION_DENIED ? "denied" : "prompt";
            setPermissionState(state);
            resolve(state);
          },
          { enableHighAccuracy: false, timeout: 15_000, maximumAge: POSITION_MAX_AGE_MS }
        );
      }),
    [persistPosition, setPermissionState]
  );

  const dismissPrompt = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(PROMPT_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const dismissFallback = useCallback(() => {
    setFallbackDismissed(true);
    try {
      window.sessionStorage.setItem(FALLBACK_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const hasGeolocation =
    typeof navigator !== "undefined" && !!navigator.geolocation;

  const shouldPrompt =
    Boolean(user) &&
    isCheckedIn &&
    !dismissed &&
    !ghostModeRef.current &&
    (permissionState === "prompt" || permissionState === "unknown") &&
    hasGeolocation;

  const shouldShowFallback =
    Boolean(user) &&
    isCheckedIn &&
    !fallbackDismissed &&
    permissionState === "denied" &&
    hasGeolocation;

  // tick para reavaliar staleness
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const status: BackgroundLocationStatus = useMemo(() => {
    if (!user || !hasGeolocation || ghostModeRef.current || !isCheckedIn) return "disabled";
    if (permissionState === "denied") return "denied";
    if (permissionState !== "granted") return "pending";
    if (!lastUpdateAt) return "pending";
    if (Date.now() - lastUpdateAt > FOREGROUND_UPDATE_INTERVAL_MS * 2) return "stale";
    return "active";
  }, [user, hasGeolocation, permissionState, lastUpdateAt, ghostModeRef, isCheckedIn]);

  // ---- Retry com backoff ao ficar "pending" ----
  useEffect(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (status !== "pending") {
      retryAttemptRef.current = 0;
      return;
    }
    if (!userId || !hasGeolocation || ghostModeRef.current || !isCheckedIn) return;
    if (permissionState === "denied") return;

    const MAX_ATTEMPTS = 5;
    const attempt = retryAttemptRef.current;
    if (attempt >= MAX_ATTEMPTS) return;

    const delay = Math.min(2000 * 2 ** attempt, 32_000);
    retryTimerRef.current = setTimeout(() => {
      retryAttemptRef.current = attempt + 1;
      void logLocationTransition(userId, {
        kind: "status",
        from: "pending",
        to: "pending-retry",
        context: { attempt: attempt + 1, delayMs: delay, permissionState },
      });
      probePositionForRetry();
    }, delay);

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [status, userId, hasGeolocation, permissionState, ghostModeRef, probePositionForRetry, isCheckedIn]);

  // ---- Debug: registra transições de permissionState e status ----
  const prevPermRef = useRef<PermissionState | "unknown">(permissionState);
  useEffect(() => {
    const from = prevPermRef.current;
    if (from !== permissionState) {
      void logLocationTransition(userId, {
        kind: "permissionState",
        from: String(from),
        to: String(permissionState),
        context: { probed, hasGeolocation, ghostMode: ghostModeRef.current },
      });
      prevPermRef.current = permissionState;
    }
  }, [permissionState, userId, probed, hasGeolocation, ghostModeRef]);

  const prevStatusRef = useRef<BackgroundLocationStatus>(status);
  useEffect(() => {
    const from = prevStatusRef.current;
    if (from !== status) {
      void logLocationTransition(userId, {
        kind: "status",
        from,
        to: status,
        context: {
          permissionState,
          lastUpdateAt,
          ageMs: lastUpdateAt ? Date.now() - lastUpdateAt : null,
        },
      });
      prevStatusRef.current = status;
    }
  }, [status, userId, permissionState, lastUpdateAt]);

  return {
    shouldPrompt,
    shouldShowFallback,
    requestPermission,
    dismissPrompt,
    dismissFallback,
    status,
    lastUpdateAt,
    permissionState,
  };
};

export const BackgroundLocationProvider = ({ children }: { children: ReactNode }) => {
  const value = useBackgroundLocationInternal();
  return (
    <BackgroundLocationContext.Provider value={value}>
      {children}
    </BackgroundLocationContext.Provider>
  );
};

export const useBackgroundLocation = (): UseBackgroundLocationResult =>
  useContext(BackgroundLocationContext);
